// See services/sosInventoryService/README.md for information about the structure
// of schema.js files.
import { Decimal, Money } from "classes/DecimalClasses";

import { i18n } from "services/i18nService";
import {
  getSalesTotal,
  getSalesSubTotal,
} from "services/sosInventoryService/salesTransaction/taxAndTotals";
import { formatDate } from "services/utility/dates";
import {
  extractName,
  formatCompanyInfo,
  formatMoneyWithAdornments,
  formatDocumentInfo,
  formatNumberLinkField,
  truncate,
  formatSync,
  formatBooleanToYesOrBlank,
  formatQuantityWithUom,
  extractDescription,
  formatMoney,
  formatLinkableItem,
  formatMoneyUnroundedWithAdornments,
} from "services/utility/formatting";
import { formatLineDecimal } from "services/utility/list";
import { parseHTML } from "services/utility/parsing";
import { starColumn } from "services/utility/schema";
import { TYPES } from "services/utility/schema";

import { NO_REF_NUMBER_STRING } from "appConstants";
import {
  DEFAULT_DECIMALS_UNROUNDED,
  DEFAULT_DECIMALS_ROUNDED,
} from "appConstants";

export const columnInfo = [
  starColumn,
  {
    name: "number",
    fixed: true,
    heading: i18n("columns.ReferenceNumber"),
    formatFunc: formatNumberLinkField,
    minWidth: "7em",
    linkField: true,
    sortable: true,
  },
  {
    name: "customer",
    heading: i18n("columns.Customer"),
    formatFunc: extractName,
    minWidth: "10em",
    maxWidth: "25em",
    sortable: true,
    default: true,
  },
  {
    name: "date",
    heading: i18n("columns.TxnDate"),
    formatFunc: formatDate,
    defaultSort: true,
    sortDirection: "asc",
    minWidth: "8em",
    sortable: true,
    default: true,
  },
  {
    name: "expiration",
    heading: i18n("columns.ExpDate"),
    formatFunc: formatDate,
    minWidth: "8em",
    sortable: true,
    default: true,
  },
  {
    name: "status",
    heading: i18n("columns.Status"),
    sortable: true,
    default: true,
  },
  {
    name: "comment",
    heading: i18n("columns.Comment"),
    formatFunc: [parseHTML, truncate],
    downloadFn: parseHTML,
    minWidth: "25em",
    maxWidth: "25em",
    sortable: true,
    default: true,
  },
  {
    name: "documents",
    heading: i18n("columns.Documents"),
    formatFunc: formatDocumentInfo,
    minWidth: "15em",
    default: true,
  },
  {
    name: "shipping",
    heading: i18n("columns.ShipTo"),
    formatFunc: formatCompanyInfo,
    minWidth: "10em",
    maxWidth: "15em",
  },
  {
    name: "salesRep",
    heading: i18n("columns.SalesRep"),
    formatFunc: extractName,
    minWidth: "8em",
    sortable: true,
  },
  {
    name: "total",
    heading: i18n("columns.TotalAmt"),
    formatFunc: formatMoneyWithAdornments,
    align: "right",
    sortable: true,
  },
  {
    name: "channel",
    heading: i18n("columns.Channel"),
    formatFunc: extractName,
    sortable: true,
  },
  {
    name: "sync",
    fieldName: "syncMessage",
    heading: i18n("columns.Sync"),
    formatFunc: formatSync,
    sortable: true,
  },
  {
    name: "department",
    heading: i18n("columns.Department"),
    formatFunc: extractName,
    sortable: true,
  },
  {
    name: "customerNotes",
    heading: i18n("columns.CustomerNotes"),
    formatFunc: [parseHTML, truncate],
    downloadFn: parseHTML,
    minWidth: "25em",
    maxWidth: "25em",
  },
  {
    name: "billing",
    heading: i18n("columns.Billing"),
    formatFunc: formatCompanyInfo,
  },
  {
    name: "customerMessage",
    heading: i18n("columns.CustomerMessage"),
    formatFunc: [parseHTML, truncate],
    downloadFn: parseHTML,
    minWidth: "25em",
    maxWidth: "25em",
  },
  {
    name: "hasSignature",
    heading: i18n("columns.Signature"),
    formatFunc: formatBooleanToYesOrBlank,
  },
];

export function getEmptyRecord(settings) {
  return {
    acceptedBy: null,
    acceptedDate: null,
    archived: false,
    status: "Pending",
    billing: {
      company: null,
      contact: null,
      phone: null,
      email: null,
      addressName: null,
      addressType: null,
      address: {
        line1: null,
        line2: null,
        line3: null,
        line4: null,
        line5: null,
        city: null,
        stateProvince: null,
        postalCode: null,
        country: null,
      },
    },
    channel: null,
    comment: null,
    currency: settings.homeCurrencyRef,
    customer: null,
    customerMessage: null,
    customerNotes: null,
    customFields: [],
    date: new Date(),
    dateChanged: false,
    department: null,
    discountAmount: new Money(0),
    discountPercent: Decimal.ZERO,
    exchangeRate: new Decimal(1.0),
    discountTaxable: settings.discountTaxable,
    documents: [],
    expiration: null,
    hasSignature: false,
    keys: null,
    lines: [],
    number: "",
    originalDate: "",
    salesRep: null,
    shipping: {
      company: null,
      contact: null,
      phone: null,
      email: null,
      addressName: null,
      addressType: null,
      address: {
        line1: null,
        line2: null,
        line3: null,
        line4: null,
        line5: null,
        city: null,
        stateProvince: null,
        postalCode: null,
        country: null,
      },
    },
    shippingAmount: new Money(0),
    shippingTaxable: false,
    starred: 0,
    subTotal: new Money(0),
    summaryOnly: false,
    taxAmount: new Money(0),
    taxCode: settings.defaultTaxCode,
    taxPercent: Decimal.ZERO,
    total: new Money(0),
    transactionLocationQuickBooks: null,
    syncMessage: null,
    values: null,
  };
}

export const LINE_ITEM_METADATA = {
  lineNumber: {},
  item: {
    type: TYPES.reference,
    initialValue: { id: null, name: null, description: null },
  },
  class: { type: TYPES.reference },
  bin: { type: TYPES.reference },
  jobWorkcenter: { type: TYPES.reference },
  taxCode: { type: TYPES.reference },
  description: { type: TYPES.text },
  quantity: {
    type: TYPES.decimal,
    placesRight: DEFAULT_DECIMALS_UNROUNDED,
    initialValue: Decimal.ZERO,
  },
  weight: { type: TYPES.decimal, initialValue: Decimal.ZERO },
  weightunit: { type: TYPES.text },
  volume: { type: TYPES.decimal, initialValue: Decimal.ZERO },
  volumeunit: { type: TYPES.text },
  unitprice: {
    type: TYPES.money,
    currency: true,
    placesRight: DEFAULT_DECIMALS_UNROUNDED,
    editStr: formatLineDecimal,
    initialValue: new Money(0),
  },
  amount: {
    type: TYPES.money,
    currency: true,
    placesRight: DEFAULT_DECIMALS_ROUNDED,
    editStr: formatLineDecimal,
    initialValue: new Money(0),
  },
  altAmount: {
    type: TYPES.money,
    currency: true,
    placesRight: DEFAULT_DECIMALS_UNROUNDED,
    editStr: formatLineDecimal,
    initialValue: new Money(0),
  },
  cost: {
    type: TYPES.money,
    currency: true,
    placesRight: DEFAULT_DECIMALS_UNROUNDED,
    editStr: formatLineDecimal,
    initialValue: new Money(0),
  },
  uom: { type: TYPES.reference },
  serials: { type: TYPES.arrayOfReferences, initialValue: null },
  lot: { type: TYPES.reference },
  picked: { type: TYPES.decimal, initialValue: Decimal.ZERO },
  shipped: { type: TYPES.decimal, initialValue: Decimal.ZERO },
  invoiced: {
    type: TYPES.decimal,
    editStr: formatLineDecimal,
    initialValue: Decimal.ZERO,
  },
  produced: { type: TYPES.decimal, initialValue: Decimal.ZERO },
  returned: { type: TYPES.decimal, initialValue: Decimal.ZERO },
  listPrice: {
    type: TYPES.money,
    currency: true,
    placesRight: DEFAULT_DECIMALS_UNROUNDED,
    editStr: formatLineDecimal,
    initialValue: new Money(0),
  },
  margin: {
    type: TYPES.decimal,
    placesRight: DEFAULT_DECIMALS_ROUNDED,
    initialValue: Decimal.ZERO,
  },
  percentdiscount: {
    type: TYPES.decimal,
    placesRight: DEFAULT_DECIMALS_ROUNDED,
    initialValue: Decimal.ZERO,
  },
  available: {
    placesRight: DEFAULT_DECIMALS_UNROUNDED,
    editStr: formatLineDecimal,
    initialValue: Decimal.ZERO,
  },
  onhand: {
    type: TYPES.itemField,
    editStr: formatLineDecimal,
    initialValue: Decimal.ZERO,
  },
  itemDetails: { initialValue: {} },
  relatedRecords: { initialValue: {} },
};

export const FIELDS_TO_TRANSFORM = {
  DECIMAL_FIELDS: ["exchangeRate", "discountPercent", "taxPercent"],
  MONEY_FIELDS: [
    "depositAmount",
    "discountAmount",
    "taxAmount",
    "total",
    "shippingAmount",
    "subTotal",
  ],
  DATE_FIELDS: ["expiration", "acceptedDate"],
  DATETIME_FIELDS: ["lastSync"],
  NON_UTC_DATETIME_FIELDS: ["date"],
};

export const REFERENCE_FIELDS = ["customer"];

export const LINE_ACTIONS = [
  ["quickView", "pdf", "email"],
  ["salesOrder", "invoice", "salesReceipt"],
  "template",
  "archive",
  "openClose",
  "delete",
  "history",
  "sync",
];
export const BATCH_ACTIONS = [
  ["pdf", "email"],
  "delete",
  "archive",
  "openClose",
  "sync",
];

export const ITEM_CALCULATED_FIELDS = ["available", "salesPrice"];

export const QUICK_VIEW_CONFIG = {
  titleSetting: "estimateTitle",
  footerSetting: "estimateFooter",
  isSalesTransaction: true,
  addressConfig: [
    { name: "billing", label: i18n("address.BillTo") },
    { name: "shipping", label: i18n("address.ShipTo") },
  ],
  tableConfig: [{ type: "lines" }],
  headerFields: [
    {
      name: "date",
      formatFunc: formatDate,
      label: i18n("columns.Date"),
    },
    {
      name: "number",
      label: i18n("frmLabel.EstimateNumber"),
      defaultValue: NO_REF_NUMBER_STRING,
    },
  ],
  fields: [
    {
      name: "customerMessage",
      label: i18n("frmLabel.CustomerMessage"),
    },
    {
      name: "status",
      label: i18n("frmLabel.Status"),
    },
  ],
  lines: [
    {
      name: "item",
      formatFunc: formatLinkableItem,
      heading: i18n("columns.Item"),
      align: "left",
      isItemField: true,
    },
    {
      name: "description",
      formatFunc: extractDescription,
      heading: i18n("columns.Description"),
      align: "left",
    },
    {
      name: "quantity",
      formatFunc: formatQuantityWithUom,
      heading: i18n("columns.Quantity"),
      align: "right",
    },
    {
      name: "unitprice",
      formatFunc: formatMoneyUnroundedWithAdornments,
      heading: i18n("columns.Rate"),
      align: "right",
    },
    {
      name: "amount",
      formatFunc: formatMoney,
      heading: i18n("columns.Amount"),
      align: "right",
      isTotalColumn: true,
    },
  ],
  tableTotals: [
    {
      label: i18n("quickView.SUBTOTAL"),
      formatFunc: getSalesSubTotal,
      alwaysShow: true,
    },
    {
      label: i18n("quickView.DISCOUNT"),
      field: "discountAmount",
    },
    {
      label: i18n("quickView.SHIPPING"),
      field: "shippingAmount",
    },
    {
      label: i18n("quickView.TAX"),
      field: "taxAmount",
    },
    {
      label: i18n("quickView.TOTAL"),
      formatFunc: getSalesTotal,
      alwaysShow: true,
    },
  ],
};
